import type { BranchKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useCallback } from 'react'

import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { useAutomaticWorkspaceName } from './useAutomaticWorkspaceName'

export const useCreateAutomaticWorkspace = ({
  workspaceNamePrefix,
  branchKey,
}: {
  workspaceNamePrefix?: string
  branchKey: Optional<BranchKey>
}) => {
  const { onCreate, onSwitch } = useWorkspaceManagerContext()
  const workspaceName = useAutomaticWorkspaceName({
    workspaceNamePrefix,
    server: branchKey?.server,
  })

  const create = useCallback(
    async ({
      shouldInvalidateCache,
      shouldSwitchWorkspace,
    }: {
      shouldInvalidateCache?: boolean
      shouldSwitchWorkspace?: boolean
    }) => {
      if (branchKey) {
        const workspaceKey = { name: workspaceName, branchKey }
        await onCreate(workspaceKey, shouldInvalidateCache)

        if (shouldSwitchWorkspace) {
          await onSwitch(workspaceKey)
        }

        return workspaceKey
      } else {
        throw new Error(
          'A defined branch key is required to create an automatic workspace.',
        )
      }
    },
    [onCreate, onSwitch, workspaceName, branchKey],
  )

  return [workspaceName, create] as const
}
